/**
 * AdminLayout.jsx
 * Dark sidebar layout — matches the auth design system.
 * Tokens: #0c1118 dark, #f9ab14 amber, Bebas Neue + DM Sans + DM Mono
 *
 * Changes v2:
 *  - Removed sidebar Sign Out button (Sign Out is only in profile dropdown)
 *  - Removed blueprint grid from sidebar background
 *  - Full mobile responsiveness via CSS class-based approach
 */

import { useEffect, useRef, useState } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  LogOut,
  Shield,
  Bell,
  User,
  LayoutDashboard,
  Users,
  FolderOpen,
  Package,
  CreditCard,
  BarChart3,
  FileText,
  Sofa,
  ShoppingBag,
  Settings,
  Menu,
  X,
  ChevronDown,
  Box,
  Key,
  Monitor,
} from "lucide-react";
import NotificationBell from "../components/NotificationBell";
import { useAuth } from "../auth/AuthContext";
import { auth } from "../services/auth";
import { can, getUserType } from "../config/roles";
import { NAV_CONFIG } from "../config/navigation";

/* ── Design tokens ────────────────────────────────────────────────── */
const T = {
  fontDisplay: "'DM Sans', sans-serif",
  fontBody: "'DM Sans', sans-serif",
  fontMono: "'DM Sans', sans-serif",
  amber: "#ffffff",
  amberDark: "#e0e0e0",
  dark: "#0c1118",
  darkBorder: "rgba(255,255,255,0.07)",
  darkText: "#c8dae4",
  darkTextSoft: "#4a6070",
  white: "#ffffff",
  panelW: 256,
  panelWCol: 68,
  headerH: 60,
};

/* ── Inject fonts ──────────────────────────────────────────────────── */
if (typeof document !== "undefined" && !document.getElementById("adm-fonts")) {
  const l = document.createElement("link");
  l.id = "adm-fonts";
  l.rel = "stylesheet";
  l.href =
    "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
  document.head.appendChild(l);
}

/* ── Inject global styles + keyframes ─────────────────────────────── */
if (typeof document !== "undefined" && !document.getElementById("adm-styles")) {
  const s = document.createElement("style");
  s.id = "adm-styles";
  s.textContent = `
    @keyframes adm-pulse {
      0%,100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.25); }
      50%      { box-shadow: 0 0 0 5px rgba(255,255,255,0); }
    }
    @keyframes adm-slide-in {
      from { opacity:0; transform:translateX(-8px); }
      to   { opacity:1; transform:translateX(0); }
    }
    @keyframes adm-fade-up {
      from { opacity:0; transform:translateY(5px); }
      to   { opacity:1; transform:translateY(0); }
    }

    /* ── Sidebar ── */
    .adm-sidebar {
      position: fixed;
      left: 0; top: 0; bottom: 0;
      background: #0c1118;
      border-right: 1px solid rgba(255,255,255,0.07);
      display: flex;
      flex-direction: column;
      z-index: 50;
      transition: width 0.25s cubic-bezier(0.4,0,0.2,1),
                  transform 0.28s cubic-bezier(0.4,0,0.2,1);
      overflow: hidden;
    }

    /* ── Content area ── */
    .adm-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 100vh;
      min-width: 0;
      max-width: 100%;
      overflow-x: hidden;
      transition: margin-left 0.25s cubic-bezier(0.4,0,0.2,1);
    }

    /* Desktop-only elements */
    .adm-desktop-only { display: flex !important; }
    /* Mobile-only elements */
    .adm-mobile-only  { display: none  !important; }

    /* ── Mobile breakpoint ── */
    @media (max-width: 768px) {
      /* Sidebar becomes a slide-in drawer at full width */
      .adm-sidebar {
        width: 256px !important;
        transform: translateX(-100%);
        box-shadow: none;
      }
      .adm-sidebar.open {
        transform: translateX(0);
        box-shadow: 8px 0 40px rgba(0,0,0,0.5);
      }

      /* Content takes full width */
      .adm-content { margin-left: 0 !important; }

      /* Header spans full width on mobile */
      .adm-header { left: 0 !important; }

      /* Swap visibility */
      .adm-desktop-only { display: none  !important; }
      .adm-mobile-only  { display: flex  !important; }

      /* Hide profile name text on tiny screens */
      .adm-profile-name { display: none !important; }
    }

    /* Thin scrollbar inside sidebar */
    .adm-nav-scroll::-webkit-scrollbar { width: 3px; }
    .adm-nav-scroll::-webkit-scrollbar-track { background: transparent; }
    .adm-nav-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 99px; }
  `;
  document.head.appendChild(s);
}

/* ── Icon map ──────────────────────────────────────────────────────── */
function iconFor(path) {
  if (path === "/admin") return <LayoutDashboard size={18} />;
  if (path.includes("/users")) return <Users size={18} />;
  if (path.includes("/projects")) return <FolderOpen size={18} />;
  if (path.includes("/assets")) return <Package size={18} />;
  if (path.includes("/subscriptions")) return <CreditCard size={18} />;
  if (path.includes("/purchase-plans")) return <ShoppingBag size={18} />;
  if (path.includes("/reports")) return <BarChart3 size={18} />;
  if (path.includes("/quotes")) return <FileText size={18} />;
  if (path.includes("/furnitures")) return <Sofa size={18} />;
  if (path.includes("/revit"))           return <Key     size={18} />;
  if (path.includes("/editor-settings")) return <Monitor size={18} />;
  return <LayoutDashboard size={18} />;
}

/* ════════════════════════════════════════════════════════════════════
   MAIN LAYOUT
   ════════════════════════════════════════════════════════════════════ */
export default function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Close drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // ESC closes drawer
  useEffect(() => {
    if (!mobileOpen) return;
    const fn = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [mobileOpen]);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const sideW = collapsed ? T.panelWCol : T.panelW;

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        background: "#f4f6f8",
        fontFamily: T.fontBody,
        overflowX: "hidden",
        maxWidth: "100vw",
      }}
    >
      {/* ── Mobile backdrop ─────────────────────────────────────────── */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 48,
            background: "rgba(0,0,0,0.65)",
            backdropFilter: "blur(3px)",
            WebkitBackdropFilter: "blur(3px)",
          }}
        />
      )}

      {/* ══════════════════════════════════════════════════════════════
          SIDEBAR
          ══════════════════════════════════════════════════════════════ */}
      <aside
        className={`adm-sidebar${mobileOpen ? " open" : ""}`}
        style={{ width: sideW }}
      >
        {/* Subtle amber glow at bottom */}
        <div
          style={{
            position: "absolute",
            bottom: -100,
            left: -60,
            pointerEvents: "none",
            width: 280,
            height: 280,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 65%)",
          }}
        />

        {/* ── Brand header ─────────────────────────────────────────── */}
        <div
          style={{
            height: T.headerH,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "space-between",
            padding: collapsed ? "0 16px" : "0 16px 0 18px",
            borderBottom: `1px solid ${T.darkBorder}`,
          }}
        >
          {/* Logo + wordmark */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              minWidth: 0,
            }}
          >
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 8,
                flexShrink: 0,
                background: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "none",
              }}
            >
              <Shield size={16} style={{ color: T.dark }} />
            </div>

            {!collapsed && (
              <div style={{ animation: "adm-slide-in 0.2s ease", minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: T.fontBody,
                    fontSize: "1.05rem",
                    letterSpacing: "0.04em",
                    color: T.white,
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  My Designer<span style={{ color: "rgba(255,255,255,0.5)" }}>Plus</span>
                </div>
                <div
                  style={{
                    fontFamily: T.fontBody,
                    fontSize: "0.51rem",
                    letterSpacing: "0.18em",
                    color: T.darkTextSoft,
                    marginTop: 2,
                  }}
                >
                  Admin Console
                </div>
              </div>
            )}
          </div>

          {/* Desktop: collapse toggle */}
          <button
            className="adm-desktop-only"
            onClick={() => setCollapsed((v) => !v)}
            title={collapsed ? "Expand" : "Collapse"}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: T.darkTextSoft,
              padding: 4,
              borderRadius: 6,
              flexShrink: 0,
              transition: "color 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = T.amber)}
            onMouseLeave={(e) => (e.currentTarget.style.color = T.darkTextSoft)}
          >
            {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
          </button>

          {/* Mobile: close (X) button inside drawer */}
          <button
            className="adm-mobile-only"
            onClick={() => setMobileOpen(false)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: T.darkTextSoft,
              padding: 4,
              borderRadius: 6,
              flexShrink: 0,
              transition: "color 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = T.amber)}
            onMouseLeave={(e) => (e.currentTarget.style.color = T.darkTextSoft)}
          >
            <X size={17} />
          </button>
        </div>

        {/* ── Navigation ───────────────────────────────────────────── */}
        <div
          className="adm-nav-scroll"
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "10px 8px",
          }}
        >
          <SidebarNav collapsed={collapsed} />
        </div>

        {/* ── Footer: sign out + copyright ─────────────────────────── */}
        <div
          style={{
            borderTop: `1px solid ${T.darkBorder}`,
            padding: collapsed ? "10px 8px" : "10px 8px 12px",
            flexShrink: 0,
          }}
        >
          <SidebarSignOut collapsed={collapsed} />
          {!collapsed && (
            <p
              style={{
                fontFamily: T.fontBody,
                fontSize: "0.5rem",
                letterSpacing: "0.08em",
                color: T.darkTextSoft,
                textAlign: "center",
                margin: "10px 0 0",
              }}
            >
              © 2025 MyDesignerPlus Inc.
            </p>
          )}
        </div>
      </aside>

      {/* ══════════════════════════════════════════════════════════════
          MAIN CONTENT
          ══════════════════════════════════════════════════════════════ */}
      <div className="adm-content" style={{ marginLeft: sideW }}>
        {/* ── Fixed topbar ─────────────────────────────────────────── */}
        <header
          className="adm-header"
          style={{
            position: "fixed",
            top: 0,
            left: sideW,
            right: 0,
            zIndex: 40,
            height: T.headerH,
            background: "rgba(255,255,255,0.94)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            borderBottom: "1px solid #e8ecf0",
            display: "flex",
            alignItems: "center",
            padding: "0 20px",
            justifyContent: "space-between",
            gap: 12,
            transition: "left 0.25s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          {/* Left: hamburger (mobile) + breadcrumb */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              minWidth: 0,
              flex: 1,
            }}
          >
            {/* Mobile hamburger */}
            <button
              className="adm-mobile-only"
              onClick={() => setMobileOpen(true)}
              style={{
                background: "none",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                cursor: "pointer",
                color: "#6b7280",
                padding: "6px",
                flexShrink: 0,
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#111111";
                e.currentTarget.style.color = "#111111";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.color = "#6b7280";
              }}
            >
              <Menu size={18} />
            </button>

            <Breadcrumb />
          </div>

          {/* Right: notifications + profile */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexShrink: 0,
            }}
          >
            <NotificationBell admin />
            <AdminProfileMenu />
          </div>
        </header>

        {/* ── Page output ──────────────────────────────────────────── */}
        <main
          style={{
            flex: 1,
            padding: `${T.headerH + 24}px 24px 48px`,
            animation: "adm-fade-up 0.22s ease",
            boxSizing: "border-box",
            width: "100%",
            minWidth: 0,
            overflowX: "hidden",
          }}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   SIDEBAR SIGN OUT
   ════════════════════════════════════════════════════════════════════ */
function SidebarSignOut({ collapsed }) {
  const [hovered, setHovered] = useState(false);

  async function handleSignOut() {
    try {
      await auth.logout();
    } catch {}
    window.location.replace("/login");
  }

  return (
    <button
      onClick={handleSignOut}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={collapsed ? "Sign Out" : undefined}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: collapsed ? "11px 0" : "9px 11px",
        justifyContent: collapsed ? "center" : "flex-start",
        borderRadius: 8,
        border: "none",
        cursor: "pointer",
        background: hovered ? "rgba(239,68,68,0.12)" : "transparent",
        transition: "background 0.14s",
      }}
    >
      <LogOut
        size={18}
        style={{
          color: hovered ? "#ef4444" : T.darkTextSoft,
          flexShrink: 0,
          transition: "color 0.14s",
        }}
      />
      {!collapsed && (
        <span
          style={{
            fontFamily: T.fontBody,
            fontSize: "0.845rem",
            fontWeight: 500,
            color: hovered ? "#ef4444" : T.darkTextSoft,
            transition: "color 0.14s",
            whiteSpace: "nowrap",
          }}
        >
          Sign Out
        </span>
      )}
    </button>
  );
}

/* ════════════════════════════════════════════════════════════════════
   SIDEBAR NAV
   ════════════════════════════════════════════════════════════════════ */
function SidebarNav({ collapsed }) {
  const { user } = useAuth();
  const type = getUserType(user);
  const items = (NAV_CONFIG[type] || []).filter((i) => can(user, i.perm));

  return (
    <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/admin"}
          title={collapsed ? item.label : undefined}
          style={{ textDecoration: "none" }}
        >
          {({ isActive }) => (
            <NavItem
              isActive={isActive}
              collapsed={collapsed}
              path={item.to}
              label={item.label}
            />
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function NavItem({ isActive, collapsed, path, label }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: collapsed ? "11px 0" : "9px 11px",
        justifyContent: collapsed ? "center" : "flex-start",
        borderRadius: 8,
        cursor: "pointer",
        position: "relative",
        transition: "background 0.14s, border-color 0.14s",
        background: isActive
          ? "rgba(255,255,255,0.08)"
          : hovered
            ? "rgba(255,255,255,0.04)"
            : "transparent",
        border: `1px solid ${isActive ? "rgba(255,255,255,0.12)" : "transparent"}`,
      }}
    >
      {/* Active left bar */}
      {isActive && (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: "20%",
            bottom: "20%",
            width: 3,
            borderRadius: "0 3px 3px 0",
            background: "#ffffff",
            boxShadow: "none",
          }}
        />
      )}

      {/* Icon */}
      <span
        style={{
          color: isActive ? T.amber : hovered ? T.darkText : T.darkTextSoft,
          display: "flex",
          flexShrink: 0,
          transition: "color 0.14s",
        }}
      >
        {iconFor(path)}
      </span>

      {/* Label */}
      {!collapsed && (
        <span
          style={{
            fontFamily: T.fontBody,
            fontWeight: isActive ? 600 : 400,
            fontSize: "0.845rem",
            color: isActive ? T.amber : T.darkText,
            flex: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            transition: "color 0.14s",
          }}
        >
          {label}
        </span>
      )}

      {/* Active pulse dot */}
      {isActive && !collapsed && (
        <div
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: T.amber,
            flexShrink: 0,
            animation: "adm-pulse 2s ease infinite",
          }}
        />
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   BREADCRUMB
   ════════════════════════════════════════════════════════════════════ */
function Breadcrumb() {
  const location = useLocation();
  const parts = location.pathname
    .replace(/^\/admin\/?/, "")
    .split("/")
    .filter(Boolean);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <Link
        to="/admin"
        style={{
          fontFamily: T.fontBody,
          fontSize: "0.61rem",
          letterSpacing: "0.12em",
          color: "#9ca3af",
          textDecoration: "none",
          flexShrink: 0,
          transition: "color 0.15s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#374151")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
      >
        Admin
      </Link>
      {parts.map((p, i) => (
        <span
          key={i}
          style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}
        >
          <span style={{ color: "#d1d5db", fontSize: "0.6rem", flexShrink: 0 }}>
            /
          </span>
          <span
            style={{
              fontFamily: T.fontBody,
              fontSize: "0.61rem",
              letterSpacing: "0.1em",
              color: i === parts.length - 1 ? "#111827" : "#9ca3af",
              fontWeight: i === parts.length - 1 ? 600 : 400,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {p.replace(/-/g, " ")}
          </span>
        </span>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   PROFILE MENU
   ════════════════════════════════════════════════════════════════════ */
function AdminProfileMenu() {
  const [open, setOpen] = useState(false);
  const [me, setMe] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onOut = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onOut);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onOut);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
          {
            credentials: "include",
            cache: "no-store",
          },
        );
        if (r.ok) {
          const d = await r.json();
          if (!dead) setMe(d.safeUser);
        }
      } catch {}
    })();
    return () => {
      dead = true;
    };
  }, []);

  const displayName =
    me?.firstname || me?.lastname
      ? `${me?.firstname || ""} ${me?.lastname || ""}`.trim()
      : me?.email || "Admin";
  const initials = displayName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  const avatarSrc = me?.avatarUrl;

  async function handleSignOut() {
    setOpen(false);
    try {
      await auth.logout();
    } catch {}
    window.location.replace("/login");
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Trigger */}
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "transparent",
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          padding: "5px 10px 5px 5px",
          cursor: "pointer",
          transition: "all 0.15s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "#111111";
          e.currentTarget.style.boxShadow = "0 0 0 3px rgba(0,0,0,0.08)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "#e5e7eb";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        <Avatar src={avatarSrc} initials={initials} size={30} />

        <div
          className="adm-profile-name"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <span
            style={{
              fontFamily: T.fontBody,
              fontSize: "0.8rem",
              fontWeight: 600,
              color: "#111827",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
            }}
          >
            {displayName}
          </span>
          <span
            style={{
              fontFamily: T.fontBody,
              fontSize: "0.51rem",
              letterSpacing: "0.12em",
              color: "#9ca3af",
            }}
          >
            Administrator
          </span>
        </div>

        <ChevronDown
          size={13}
          style={{
            color: "#9ca3af",
            marginLeft: 2,
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.2s",
          }}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 49 }}
            onClick={() => setOpen(false)}
          />
          <div
            style={{
              position: "absolute",
              right: 0,
              top: "calc(100% + 8px)",
              width: 252,
              zIndex: 50,
              background: "#fff",
              border: "1px solid #e8ecf0",
              borderRadius: 12,
              boxShadow:
                "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
              overflow: "hidden",
              animation: "adm-fade-up 0.15s ease",
            }}
          >
            {/* Dark profile header */}
            <div
              style={{
                padding: "14px 16px",
                background: T.dark,
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <Avatar src={avatarSrc} initials={initials} size={42} />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: T.fontBody,
                    fontWeight: 700,
                    fontSize: "0.88rem",
                    color: T.white,
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayName}
                </div>
                <div
                  style={{
                    fontFamily: T.fontBody,
                    fontSize: "0.51rem",
                    letterSpacing: "0.14em",
                    color: "rgba(255,255,255,0.45)",
                    marginTop: 2,
                  }}
                >
                  System Administrator
                </div>
                <div
                  style={{
                    fontFamily: T.fontBody,
                    fontSize: "0.72rem",
                    color: T.darkTextSoft,
                    marginTop: 3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {me?.email}
                </div>
              </div>
            </div>

            {/* Links */}
            <div style={{ padding: "6px" }}>
              <DropdownLink to="/admin/profile" icon={<User size={14} />}>
                Admin Profile
              </DropdownLink>
              <DropdownLink to="/admin/notifications" icon={<Bell size={14} />}>
                Notifications
              </DropdownLink>
              <DropdownLink to="/admin/editor-settings" icon={<Settings size={14} />}>
                Settings
              </DropdownLink>
            </div>

            {/* Sign out — lives only here */}
            <div style={{ borderTop: "1px solid #f0f2f5", padding: "6px" }}>
              <button
                onClick={handleSignOut}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  padding: "8px 12px",
                  borderRadius: 8,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: T.fontBody,
                  fontSize: "0.845rem",
                  color: "#dc2626",
                  textAlign: "left",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#fef2f2")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "none")
                }
              >
                <LogOut size={14} />
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ── Avatar ─────────────────────────────────────────────────────────── */
function Avatar({ src, initials, size = 36 }) {
  const [err, setErr] = useState(false);

  if (src && !err) {
    return (
      <img
        src={src}
        alt="Avatar"
        onError={() => setErr(true)}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          objectFit: "cover",
          flexShrink: 0,
          border: "2px solid rgba(255,255,255,0.2)",
        }}
      />
    );
  }

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: "linear-gradient(135deg, #333 0%, #111 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontFamily: T.fontBody,
        fontSize: size * 0.38,
        color: "#ffffff",
        letterSpacing: "0.04em",
        border: "2px solid rgba(249,171,20,0.3)",
      }}
    >
      {initials || <Shield size={size * 0.44} />}
    </div>
  );
}

/* ── Dropdown link ──────────────────────────────────────────────────── */
function DropdownLink({ to, icon, children }) {
  return (
    <Link
      to={to}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "8px 12px",
        borderRadius: 8,
        fontFamily: T.fontBody,
        fontSize: "0.845rem",
        color: "#374151",
        textDecoration: "none",
        transition: "background 0.15s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
    >
      <span style={{ color: "#9ca3af", display: "flex" }}>{icon}</span>
      {children}
    </Link>
  );
}
